/**
 * 权限管理页面
 *
 * 管理角色与权限的映射关系。管理员可以为不同角色（普通用户、只读用户等）
 * 分配/撤销权限，支持全选/取消全选，并保存到后端。
 *
 * 自定义元素：`crm-permissions-page`
 * 路由：`/crm/permissions`
 *
 * 主要功能：
 * - 查看所有权限项（卡片式布局，含中文名称、描述、权限代码）
 * - 按角色切换查看/编辑权限分配
 * - 单个权限的开关切换（Toggle）
 * - 全选/全部取消
 * - 保存权限分配
 *
 * 设计决策：
 * - admin 角色的权限不可编辑（后端限制），前端从角色列表中过滤掉
 * - 权限卡片用 PERM_LABELS 映射为中文标签，提供更好的可读性
 *
 * 依赖模块：
 * - `permissionApi`：权限 API（列表、角色权限查询、分配）
 * - `crm-select`：下拉选择组件（角色切换）
 * - `toast`：消息提示
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { permissionApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { Permission } from '../types.js';
import { toast } from '../components/toast.js';
import '../components/select.js';
import { isNetworkError } from '../utils/index.js';

/**
 * 权限代码到中文标签的映射表
 * key: 权限代码（如 'customer:manage'）
 * value: { name: 中文名称, desc: 中文描述 }
 */
const PERM_LABELS: Record<string, { name: string; desc: string }> = {
  'customer:manage':   { name: '客户管理',     desc: '客户列表、详情、分类、邮箱管理' },
  'email:manage':      { name: '邮件管理',     desc: '邮件收发、账户、签名、附件' },
  'user:manage':       { name: '用户管理',     desc: '用户创建、编辑、删除' },
  'permission:manage': { name: '权限管理',     desc: '权限和角色分配' },
  'template:manage':   { name: '邮件模板',     desc: '模板创建与管理' },
  'task:manage':       { name: '邮件任务',     desc: '批量发送任务管理、触发抓取与清理' },
  'tag:manage':        { name: '邮件标签',     desc: '标签创建与管理' },
  'blacklist:manage':  { name: '邮件黑名单',   desc: '黑名单管理与垃圾箱迁移' },
};

/**
 * 角色代码到中文标签的映射表
 */
const ROLE_LABELS: Record<string, string> = {
  user: '普通用户',
  readonly: '只读用户',
};

@customElement('crm-permissions-page')
export class CrmPermissionsPage extends BaseElement {
  static styles = css`
    /* 权限卡片样式：hover 和 active 状态 */
    .perm-card {
      transition: all 0.2s ease;
    }
    .perm-card:hover {
      border-color: color-mix(in srgb, var(--color-primary, #0d74ce) 40%, transparent);
    }
    .perm-card.active {
      border-color: color-mix(in srgb, var(--color-primary, #0d74ce) 60%, transparent);
      background: color-mix(in srgb, var(--color-primary, #0d74ce) 4%, transparent);
    }

    /* Toggle 开关样式：on 状态使用主题色，off 状态使用边框色 */
    .toggle {
      width: 36px; height: 20px;
      border-radius: 999px;
      flex-shrink: 0;
      position: relative;
      cursor: pointer;
      transition: background 0.2s ease;
    }
    .toggle.on  { background: var(--color-primary, #0d74ce); }
    .toggle.off { background: var(--color-border, #d9d9e0); }

    /* Toggle 滑块（knob）：在 on/off 状态间滑动 */
    .toggle-knob {
      position: absolute;
      top: 2px;
      width: 16px; height: 16px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.15);
      transition: left 0.2s ease;
    }
    .toggle.on  .toggle-knob { left: 18px; }
    .toggle.off .toggle-knob { left: 2px; }
  `;

  /** 所有权限列表（从后端加载） */
  @state() private permissions: Permission[] = [];
  /** 角色到权限 ID 数组的映射，key 为角色名（如 'user'），value 为已分配的权限 ID 列表 */
  @state() private roleAssignments: Record<string, number[]> = {};
  /** 当前选中的角色（默认 'user'） */
  @state() private selectedRole: string = 'user';

  /**
   * 组件挂载时触发，加载权限和角色分配数据
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  /**
   * 并行加载权限列表和角色权限分配
   *
   * 使用 Promise.all 同时请求：
   * 1. 所有权限项（per_page=100）
   * 2. 所有角色的权限分配映射
   *
   * 加载完成后，如果当前选中角色不在可用角色中，自动选中第一个非 admin 角色。
   *
   * @returns Promise<void>
   */
  private async loadData() {
    try {
      const [response, roleAssignments] = await Promise.all([
        permissionApi.list({ per_page: 100 }),
        permissionApi.getAllRolePermissions(),
      ]);
      this.permissions = response.data;
      this.roleAssignments = roleAssignments;

      // 默认选中第一个非 admin 角色
      // admin 角色的权限由后端固定配置，前端不可编辑
      const nonAdminRoles = Object.keys(roleAssignments).filter(r => r !== 'admin');
      if (nonAdminRoles.length > 0 && !nonAdminRoles.includes(this.selectedRole)) {
        this.selectedRole = nonAdminRoles[0];
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '加载数据失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 计算角色下拉选项（排除 admin 角色）
   *
   * @returns 选项数组，每项包含 label（中文名 + 角色代码）和 value（角色代码）
   */
  private get _roleOptions() {
    return Object.keys(this.roleAssignments)
      .filter(r => r !== 'admin')
      .map(role => ({
        label: ROLE_LABELS[role] ? `${ROLE_LABELS[role]} (${role})` : role,
        value: role,
      }));
  }

  /**
   * 切换单个权限的启用/禁用状态
   *
   * 在当前角色的权限列表中添加或移除指定权限 ID。
   * 通过创建新数组触发 Lit 的响应式更新。
   *
   * @param permId - 权限 ID
   */
  private togglePermission(permId: number) {
    const current = [...(this.roleAssignments[this.selectedRole] || [])];
    const idx = current.indexOf(permId);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(permId);
    this.roleAssignments = { ...this.roleAssignments, [this.selectedRole]: current };
  }

  /**
   * 全选或全部取消当前角色的权限
   *
   * 如果当前角色已拥有所有权限，则清空；否则分配所有权限。
   *
   * @returns void
   */
  private toggleAll() {
    const current = this.roleAssignments[this.selectedRole] || [];
    const allIds = this.permissions.map(p => p.id);
    if (allIds.every(id => current.includes(id))) {
      this.roleAssignments = { ...this.roleAssignments, [this.selectedRole]: [] };
    } else {
      this.roleAssignments = { ...this.roleAssignments, [this.selectedRole]: [...allIds] };
    }
  }

  /**
   * 保存当前角色的权限分配到后端
   *
   * 调用 permissionApi.assignPermissions，将选中角色的权限 ID 列表提交到后端。
   * 成功时显示成功 toast，失败时显示错误 toast。
   *
   * @returns Promise<void>
   */
  private async save() {
    try {
      await permissionApi.assignPermissions(this.selectedRole, {
        role: this.selectedRole,
        permission_ids: this.roleAssignments[this.selectedRole] || [],
      });
      toast.success('权限分配已保存');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '保存失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 渲染权限管理页面
   *
   * 页面结构：
   * 1. 顶部操作栏：标题 + 角色选择下拉 + 全选/取消按钮 + 保存按钮
   * 2. 权限卡片网格：每个权限一张卡片，含名称、描述、代码和 Toggle 开关
   *
   * @returns Lit 模板
   */
  render() {
    // 当前角色已分配的权限 ID 列表
    const assignedIds = this.roleAssignments[this.selectedRole] || [];
    // 判断是否所有权限都已启用（决定全选按钮显示"全选"还是"全部取消"）
    const allOn = this.permissions.length > 0 && this.permissions.every(p => assignedIds.includes(p.id));

    return html`
      <div class="flex flex-col h-full">
        <!-- 顶部操作栏：标题 + 角色选择 + 全选按钮 + 保存按钮 -->
        <div class="flex items-center gap-3 mb-4 flex-shrink-0">
          <span class="text-base font-semibold text-text">权限分配</span>
          <!-- 角色下拉选择器 -->
          <crm-select
            .options=${this._roleOptions}
            .value=${this.selectedRole}
            @change=${(e: CustomEvent) => { this.selectedRole = e.detail.value; }}
          ></crm-select>
          <div class="flex-1"></div>
          <!-- 全选/取消按钮：根据 allOn 状态动态切换文案 -->
          <button class="px-3 py-1.5 text-xs font-medium text-text-secondary border border-border rounded hover:bg-bg transition-colors cursor-pointer"
                  @click=${this.toggleAll}>
            ${allOn ? '全部取消' : '全选'}
          </button>
          <!-- 保存按钮：将当前角色的权限分配提交到后端 -->
          <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20"
                  @click=${this.save}>
            保存
          </button>
        </div>

        <!-- 权限卡片网格：响应式布局，小屏 1 列、中屏 2 列、大屏 3 列 -->
        <div class="flex-1 overflow-auto">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            ${this.permissions.map(p => {
              // 判断当前权限是否已分配给选中角色
              const active = assignedIds.includes(p.id);
              // 从映射表获取中文名称和描述，无映射时回退到后端返回的 name/description
              const info = PERM_LABELS[p.code] || { name: p.name, desc: p.description || '' };
              return html`
                <!-- 权限卡片：点击切换权限状态 -->
                <div class="perm-card bg-surface border border-border rounded-md p-4 cursor-pointer ${active ? 'active' : ''}"
                     @click=${() => this.togglePermission(p.id)}>
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-semibold text-text">${info.name}</div>
                      <div class="text-xs text-text-muted mt-0.5">${info.desc}</div>
                      <!-- 权限代码：等宽字体显示，便于识别 -->
                      <div class="text-[10px] font-mono text-text-muted/60 mt-1">${p.code}</div>
                    </div>
                    <!-- Toggle 开关：可视化权限的启用/禁用状态 -->
                    <div class="toggle ${active ? 'on' : 'off'}">
                      <div class="toggle-knob"></div>
                    </div>
                  </div>
                </div>`;
            })}
          </div>
        </div>
      </div>
    `;
  }
}
